import type { FrameSpec } from '../config/frames';
import type { Lang } from '../ui/i18n';
import { h, icon } from '../ui/icons';
import { hasShape, lineName, shapesIn, type CatalogueLine } from '../config/collections';
import { WORDS_BY_LANG } from '../ui/words';
import { catalogueCopy } from './copy';
import { framePhoto } from './photos';
import { rovingToolbar } from '../ui/roving';

export interface CataloguePageOptions {
  frames: readonly FrameSpec[];
  /** Pre-selected frame, e.g. from `?frame=`. */
  selectedId: string | null;
  lang: Lang;
  /** A tile was selected (or deselected): update the URL, warm the frame up. */
  onSelect: (frame: FrameSpec | null) => void;
  /** « Try it on » on the selected tile. */
  onTry: (frame: FrameSpec) => void;
  /** « Debug settings » in the footer: the try-on with the tuning panel. */
  onDebug: (frame: FrameSpec) => void;
  /** A200: collection of this page (Handmade, or Signature = data line `Classic`). */
  line?: CatalogueLine;
  /** A200: the Handmade · Signature switch — frame count of each collection, and the other page's URL. */
  lines?: Record<CatalogueLine, number>;
  lineHref?: (line: CatalogueLine, shape: string | null) => string;
  /** A200: shape filter (Shopify « Forme »): initial value and change. */
  shape?: string | null;
  onShape?: (shape: string | null) => void;
}

interface Tile { frame: FrameSpec; item: HTMLLIElement; button: HTMLButtonElement; tryLink: HTMLAnchorElement }

/** Same-page link for a frame: `?frame=…&lang=…`, plus extra flags. */
export function catalogueSearch(frameId: string | null, lang: Lang, extra: Record<string, string> = {}): string {
  const q = new URLSearchParams();
  if (frameId) q.set('frame', frameId);
  q.set('lang', lang);
  for (const [k, v] of Object.entries(extra)) q.set(k, v);
  return `?${q.toString()}`;
}

/** Plain left click: handled in the page. Modified clicks keep the link's default (new tab…). */
function plainClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * A42F — Handmade catalogue page, the entry page of the try-on app.
 * White page, intro, the full collection at the same photo size, then a small
 * footer with the debug link. Tapping a tile selects it and shows
 * « Try it on → » on the tile; tapping it again deselects it.
 */
export class CataloguePage {
  private readonly tiles = new Map<string, Tile>();
  private selected: string | null = null;
  private readonly debugLink = h('a', 'mt-cat-debug');
  private readonly count = h('span', 'mt-cat-count');
  private readonly shapeChips: HTMLButtonElement[] = [];
  /** One Tab stop for the shape chips (the one that is on). */
  private syncShapes: () => void = () => undefined;
  private readonly lineLinks = new Map<CatalogueLine, HTMLAnchorElement>();
  private shape: string = 'all';

  constructor(root: HTMLElement, private readonly opts: CataloguePageOptions) {
    const line = opts.line ?? 'Handmade';
    const t = catalogueCopy(line, opts.lang);
    const cw = WORDS_BY_LANG[opts.lang].collections;
    document.title = t.pageTitle;
    root.classList.add('mt-cat');
    root.dataset.line = line;
    root.replaceChildren();

    const intro = h('header', 'mt-cat-intro');
    // A200: Handmade · Signature switch (two pages, never one mixed list).
    if (opts.lines && opts.lineHref) {
      const nav = h('nav', 'mt-cat-lines');
      nav.setAttribute('aria-label', cw.switchLabel);
      for (const l of ['Handmade', 'Classic'] as const) {
        const a = h('a', 'mt-cat-line');
        a.dataset.line = l;
        a.href = opts.lineHref(l, null);
        a.append(h('span', 'mt-cat-line-name', lineName(l)), h('span', 'mt-cat-line-n', String(opts.lines[l])));
        if (l === line) a.setAttribute('aria-current', 'page');
        a.addEventListener('click', () => { a.href = opts.lineHref!(l, this.shape); });
        this.lineLinks.set(l, a);
        nav.append(a);
      }
      intro.append(nav);
    }
    intro.append(h('p', 'mt-cat-eyebrow', t.eyebrow), h('h1', 'mt-cat-title', t.title), h('p', 'mt-cat-lede', t.lede));
    // A200: shape filter — « Toutes » and the shapes present in this collection.
    const present = shapesIn(opts.frames);
    const shapes = h('div', 'mt-cat-shapes');
    shapes.setAttribute('aria-label', cw.shape);
    for (const value of ['all', ...present]) {
      const chip = h('button', 'mt-cat-shape');
      chip.type = 'button';
      chip.dataset.shape = value;
      chip.textContent = value === 'all' ? cw.allShapes : cw.shapes[value] ?? value;
      chip.addEventListener('click', () => this.filter(value, true));
      this.shapeChips.push(chip);
      shapes.append(chip);
    }
    shapes.hidden = present.length < 2;
    this.syncShapes = rovingToolbar(shapes, '.mt-cat-shape');

    const grid = h('ul', 'mt-cat-grid');
    grid.setAttribute('aria-label', t.gridLabel);
    for (const frame of opts.frames) {
      const name = `${frame.model} — ${frame.colorway}`;
      const item = h('li', 'mt-cat-item');
      item.dataset.frameId = frame.id;
      const media = h('div', 'mt-cat-media');
      const button = h('button', 'mt-cat-tile');
      button.type = 'button';
      button.setAttribute('aria-label', name);
      button.setAttribute('aria-pressed', 'false');
      const photo = framePhoto(frame);
      const img = h('img', photo.normalised ? 'mt-cat-photo' : 'mt-cat-photo mt-cat-photo--raw');
      img.alt = ''; img.decoding = 'async'; img.loading = 'lazy';
      img.width = 800; img.height = 480;
      img.src = photo.src;
      button.append(img);
      button.addEventListener('click', () => this.select(this.selected === frame.id ? null : frame.id, { notify: true }));

      const tryLink = h('a', 'mt-cat-try');
      tryLink.href = catalogueSearch(frame.id, opts.lang, { autostart: '1' });
      tryLink.setAttribute('aria-label', t.tryOnLabel(name));
      tryLink.append(h('span', '', t.tryOn), icon('arrow', 14));
      tryLink.hidden = true;
      tryLink.addEventListener('click', event => {
        if (!plainClick(event)) return;
        event.preventDefault();
        opts.onTry(frame);
      });
      media.append(button, tryLink);

      // Visible labels repeat the button's name: hidden from screen readers, clickable with a pointer.
      const labels = h('div', 'mt-cat-labels');
      labels.setAttribute('aria-hidden', 'true');
      labels.append(h('span', 'mt-cat-name', frame.model), h('span', 'mt-cat-colour', frame.colorway));
      labels.addEventListener('click', () => button.click());

      item.append(media, labels);
      grid.append(item);
      this.tiles.set(frame.id, { frame, item, button, tryLink });
    }

    const foot = h('footer', 'mt-cat-foot');
    this.debugLink.textContent = t.debug;
    this.debugLink.addEventListener('click', event => {
      if (!plainClick(event)) return;
      event.preventDefault();
      const frame = this.tiles.get(this.selected ?? '')?.frame ?? opts.frames[0];
      if (frame) opts.onDebug(frame);
    });
    foot.append(this.count, this.debugLink);

    root.append(intro, shapes, grid, foot);
    root.removeAttribute('aria-busy');
    this.filter(opts.shape && present.includes(opts.shape as typeof present[number]) ? opts.shape : 'all');
    this.select(opts.selectedId && this.tiles.has(opts.selectedId) ? opts.selectedId : null);
  }

  get selectedId(): string | null { return this.selected; }

  get shownShape(): string { return this.shape; }

  /** A200: shows only the frames of a shape (« all »: the whole collection). */
  filter(shape: string, notify = false): void {
    this.shape = shape;
    const t = catalogueCopy(this.opts.line ?? 'Handmade', this.opts.lang);
    const cw = WORDS_BY_LANG[this.opts.lang].collections;
    let visible = 0;
    for (const tile of this.tiles.values()) {
      const on = hasShape(tile.frame, shape);
      tile.item.hidden = !on;
      if (on) visible++;
    }
    for (const chip of this.shapeChips) chip.setAttribute('aria-pressed', String(chip.dataset.shape === shape));
    if (!this.shapeChips.some(c => c === document.activeElement)) this.syncShapes();
    this.count.textContent = shape === 'all' ? t.footer(this.opts.frames.length) : cw.filtered(visible, cw.shapes[shape] ?? shape);
    if (this.selected && this.tiles.get(this.selected)?.item.hidden) this.select(null);
    if (notify) this.opts.onShape?.(shape === 'all' ? null : shape);
  }

  /** Select a tile (null: none). `scroll` brings it into view, `focus` moves the keyboard focus to it. */
  select(id: string | null, options: { notify?: boolean; scroll?: boolean; focus?: boolean } = {}): void {
    const next = id && this.tiles.has(id) ? id : null;
    this.selected = next;
    for (const [key, tile] of this.tiles) {
      const on = key === next;
      tile.button.setAttribute('aria-pressed', String(on));
      tile.item.classList.toggle('mt-cat-item--on', on);
      tile.tryLink.hidden = !on;
    }
    const first = this.opts.frames[0];
    this.debugLink.href = catalogueSearch(next ?? first?.id ?? null, this.opts.lang, { debug: '1', autostart: '1' });
    const tile = next ? this.tiles.get(next) : undefined;
    if (tile && options.scroll) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      tile.item.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
    }
    if (tile && options.focus) tile.button.focus({ preventScroll: Boolean(options.scroll) });
    if (options.notify) this.opts.onSelect(tile?.frame ?? null);
  }
}
